// Max Markup → interface spec IR. The markup is only an authoring surface: the compiled IR is
// what gets linted, stored, approved and rendered, and the server validates it again, so the
// compiler can be permissive about form but never invents anything the IR cannot hold.
import { OPERATIONS } from '../operations.ts'
import { EXPR_SCOPES } from '../expr.ts'
import type { MaxInterfaceSpec } from '../interface.ts'
import { compileExpr, MarkupError, type NameScope, type SourcePos } from './expr.ts'
import { parseXml, type Attr, type AttrValue, type TextPart, type XmlElement, type XmlNode } from './xml.ts'
import { camel, kebab, markupSchema, singular, type Caps } from './schema.ts'

export interface MarkupIssue { line: number, col: number, message: string }
export interface CompileResult { spec: MaxInterfaceSpec | null, issues: MarkupIssue[] }

/** Elements whose text content is one prop */
export const TEXT_CONTENT: Record<string, string> = {
  text: 'text', heading: 'text', badge: 'text', callout: 'text', button: 'label', link: 'label',
  option: 'label', item: 'label', toast: 'message', confirm: 'message', span: 'text'
}

/** Inline marks inside <text> */
export const INLINE: Record<string, Record<string, unknown>> = {
  b: { strong: true }, strong: { strong: true }, code: { mono: true }, mono: { mono: true }, muted: { tone: 'muted' }, span: {}
}

/** Props evaluated once per element of a template, with its as name in scope */
export const INNER_PROPS: Record<string, string[]> = {
  list: ['selected', 'open', 'on', 'children'],
  timeline: ['time', 'intent', 'children'],
  repeat: ['children']
}

export const ACTION_TAGS: Record<string, string> = {
  set: 'setState', call: 'callOperation', confirm: 'openConfirm', navigate: 'navigate', toast: 'toast',
  refresh: 'refresh', print: 'print', copy: 'copy', 'open-panel': 'openPanel', 'close-panel': 'closePanel'
}

const DECLARATIONS = new Set(['state', 'operation', 'derive', 'component', 'panel', 'icon-def', 'on:load'])

export function pascalToSnake (name: string): string {
  return name.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()
}

export function snakeToPascal (name: string): string {
  return name.split('_').map(w => w ? w[0]!.toUpperCase() + w.slice(1) : '').join('')
}

const NUMERIC = /^-?(?:\d+(?:\.\d+)?|\.\d+)$/
const RESPONSIVE = /^(?:\d+)?(?:\s*(?:base|sm|md|lg|xl):\d+)*$/

function collapse (s: string): string {
  return s.replace(/\s+/g, ' ')
}

class Compiler {
  issues: MarkupIssue[] = []
  private schema = markupSchema()
  private operations: Record<string, string> = {}

  issue (pos: SourcePos, message: string): void {
    if (this.issues.length < 50) this.issues.push({ line: pos.line, col: pos.col, message })
  }

  private guard<T> (fn: () => T, fallback: T): T {
    try {
      return fn()
    } catch (err) {
      if (err instanceof MarkupError) this.issue({ line: err.line, col: err.col }, err.message.replace(/^\d+:\d+ /, ''))
      else throw err
      return fallback
    }
  }

  expr (src: string, scope: NameScope, pos: SourcePos): unknown {
    return compileExpr(src, scope, pos)
  }

  /** "Hello {name}" as one expression: a cat, or the single hole */
  template (parts: TextPart[], scope: NameScope): unknown {
    const pieces = parts.map(p => 'text' in p ? p.text : this.expr(p.expr, scope, p.pos))
    if (pieces.length === 1) return pieces[0]
    return { cat: pieces }
  }

  attr (value: AttrValue, caps: Caps, scope: NameScope, name: string): unknown {
    const fail = (msg: string): never => { throw new MarkupError(msg, value.pos.line, value.pos.col) }
    switch (value.kind) {
      case 'bool':
        if (caps.boolean) return true
        return fail(`${name} needs a value`)
      case 'json':
        try {
          return parseJsonAttr(value.src)
        } catch {
          return fail(`${name}: {{…}} holds JSON, e.g. {{[1, 2]}} or {{{"kind": "bar"}}}`)
        }
      case 'expr': {
        const e = this.expr(value.src, scope, value.pos)
        if (caps.computed) return { expr: e }
        if (caps.expr) return e
        if (caps.nodes || caps.actions || caps.object || caps.objects) return fail(`${name} is written as child elements`)
        // A constant written as an expression: {3}, {true}, {"md"}
        if (e === null || typeof e !== 'object') return this.attr({ kind: 'string', parts: [{ text: String(e) }], pos: value.pos }, caps, scope, name)
        return fail(`${name} takes a fixed value, not an expression`)
      }
      case 'string': {
        if (value.parts.some(p => 'expr' in p)) {
          const e = this.template(value.parts, scope)
          if (caps.computed) return { expr: e }
          if (caps.expr) return e
          return fail(`${name} takes a fixed value; {…} is not allowed here`)
        }
        const s = value.parts.map(p => (p as { text: string }).text).join('')
        if (caps.responsive && /:/.test(s) && RESPONSIVE.test(s.trim())) return responsiveMap(s, fail)
        if (caps.number && NUMERIC.test(s.trim()) && !(caps.enumValues ?? []).includes(s)) {
          const n = Number(s)
          if (caps.integer && !Number.isInteger(n)) return fail(`${name} must be a whole number`)
          return n
        }
        if (caps.boolean && (s === 'true' || s === 'false')) return s === 'true'
        if (caps.strings) return s.split(/[\s,]+/).filter(Boolean)
        if (caps.enumValues && !caps.string && !caps.expr && !caps.computed) {
          if (caps.enumValues.includes(s)) return s
          return fail(`${name} is one of: ${caps.enumValues.join(', ')}`)
        }
        if (caps.string || caps.expr || caps.computed || caps.enumValues) return s
        if (caps.json) return fail(`${name} is written as {{json}}`)
        return fail(`${name} cannot be written as text`)
      }
    }
  }

  /** Attributes of an element onto out, with the props it allows */
  attrs (el: XmlElement, props: Record<string, Caps>, scope: (prop: string) => NameScope, out: Record<string, unknown>, skip: string[] = []): void {
    for (const a of el.attrs) {
      if (skip.includes(a.name)) continue
      const prop = camel(a.name)
      const caps = props[prop]
      if (!caps) {
        this.issue(a.pos, `${a.name} is not an attribute of <${el.tag}>; allowed: ${Object.keys(props).filter(p => !props[p]!.nodes && !props[p]!.actions && !props[p]!.objects && !props[p]!.object).map(kebab).join(', ')}`)
        continue
      }
      out[prop] = this.guard(() => this.attr(a.value, caps, scope(prop), a.name), undefined)
    }
  }

  /** Text content: whitespace collapsed like HTML, holes as expressions */
  content (nodes: XmlNode[], scope: NameScope): unknown {
    const parts: TextPart[] = []
    for (const n of nodes) {
      if (n.kind === 'text') parts.push(...n.parts)
      else if (n.tag === 'br') parts.push({ text: '\n' })
    }
    const merged = mergeText(parts)
    if (!merged.length) return ''
    trimEdges(merged)
    const filtered = merged.filter(p => !('text' in p) || p.text !== '')
    if (!filtered.length) return ''
    if (filtered.every(p => 'text' in p)) return filtered.map(p => (p as { text: string }).text).join('')
    return { expr: this.template(filtered, scope) }
  }

  /** <text> with inline marks: spans */
  spans (nodes: XmlNode[], scope: NameScope): Record<string, unknown>[] {
    type Run = { parts: TextPart[], marks: Record<string, unknown> }
    const runs: Run[] = []
    const push = (parts: TextPart[], marks: Record<string, unknown>) => {
      const last = runs[runs.length - 1]
      if (last && JSON.stringify(last.marks) === JSON.stringify(marks)) last.parts.push(...parts)
      else runs.push({ parts: [...parts], marks })
    }
    for (const n of nodes) {
      if (n.kind === 'text') { push(n.parts, {}); continue }
      if (n.tag === 'br') { push([{ text: '\n' }], {}); continue }
      const marks = INLINE[n.tag]
      if (!marks) { this.issue(n.pos, `<${n.tag}> is not allowed inside <text>; inline marks are b, code, muted and span`); continue }
      const own: Record<string, unknown> = { ...marks }
      if (n.tag === 'span') this.attrs(n, pick(this.schema.nodes.text!.props.spans!.objects!, ['strong', 'mono', 'tone', 'intent']), () => scope, own)
      const inner = n.children.flatMap(c => c.kind === 'text' ? c.parts : [])
      if (n.children.some(c => c.kind === 'element')) this.issue(n.pos, 'inline marks cannot be nested')
      push(inner, own)
    }
    // Collapse whitespace across the whole line, then trim its two ends
    for (const r of runs) r.parts = mergeText(r.parts)
    if (runs.length) {
      trimEdges(runs[0]!.parts, 'start')
      trimEdges(runs[runs.length - 1]!.parts, 'end')
    }
    return runs.flatMap(r => {
      const parts = r.parts.filter(p => !('text' in p) || p.text !== '')
      if (!parts.length) return []
      const text = parts.every(p => 'text' in p) ? parts.map(p => (p as { text: string }).text).join('') : { expr: this.template(parts, scope) }
      return [{ text, ...r.marks }]
    })
  }

  node (el: XmlElement, scope: NameScope): Record<string, unknown> | null {
    let tag = el.tag
    let component: string | null = null
    if (/^[A-Z]/.test(tag)) { component = pascalToSnake(tag); tag = 'use' }
    const schema = this.schema.nodes[tag]
    if (!schema || DECLARATIONS.has(tag)) {
      this.issue(el.pos, ACTION_TAGS[tag] ? `<${tag}> is an action; put it inside a handler such as <on:click>` : `unknown element <${tag}>`)
      return null
    }
    const out: Record<string, unknown> = { type: tag }
    const as = el.attrs.find(a => a.name === 'as')
    const asName = as?.value.kind === 'string' ? as.value.parts.map(p => (p as { text?: string }).text ?? '').join('') : null
    if (asName && (EXPR_SCOPES as readonly string[]).includes(asName)) this.issue(as!.pos, `as="${asName}" is a built-in scope name; choose another, e.g. entry`)
    const inner: NameScope = INNER_PROPS[tag] && asName ? { ...scope, loops: [...scope.loops, asName] } : scope
    const scopeFor = (prop: string) => INNER_PROPS[tag]?.includes(prop) ? inner : scope

    if (tag === 'use') {
      if (component) out.component = component
      const withProps: Record<string, unknown> = {}
      for (const a of el.attrs) {
        if (a.name === 'component' && !component) {
          out.component = this.guard(() => this.attr(a.value, schema.props.component!, scope, a.name), undefined)
          continue
        }
        if (['visible', 'width', 'hide-below', 'hide-from', 'hideBelow', 'hideFrom', 'id', 'span'].includes(a.name)) {
          this.attrs({ ...el, attrs: [a] }, schema.props, () => scope, out)
          continue
        }
        withProps[a.name] = this.guard(() => this.inputValue(a, scope, null, null), undefined)
      }
      if (Object.keys(withProps).length) out.with = withProps
    } else {
      this.attrs(el, schema.props, scopeFor, out)
    }

    const textProp = TEXT_CONTENT[tag]
    const hasText = el.children.some(c => c.kind === 'text')
    const inlineMarks = tag === 'text' && el.children.some(c => c.kind === 'element' && c.tag in INLINE)
    if (inlineMarks) {
      out.spans = this.spans(el.children, scope)
    } else if (hasText) {
      if (textProp && schema.props[textProp]) {
        if (out[textProp] !== undefined) this.issue(el.pos, `<${tag}> has both a ${textProp} attribute and text content`)
        out[textProp] = this.guard(() => this.content(el.children, scope), '')
      } else {
        const t = el.children.find(c => c.kind === 'text')!
        this.issue(t.pos, `<${tag}> does not take text; ${textProp ? '' : 'wrap it in <text>'}`)
      }
    }

    const children: Record<string, unknown>[] = []
    for (const child of el.children) {
      if (child.kind !== 'element' || inlineMarks) continue
      if (child.tag === 'br' && textProp) continue
      if (child.tag.startsWith('on:')) {
        this.handler(child, schema.props, scopeFor('on'), out)
        continue
      }
      const prop = propForChild(schema.props, child.tag)
      if (prop) {
        this.childProp(child, prop, schema.props[prop]!, scopeFor(prop), out)
        continue
      }
      if (schema.props.children?.nodes) {
        const n = this.node(child, scopeFor('children'))
        if (n) children.push(n)
        continue
      }
      this.issue(child.pos, `<${child.tag}> is not allowed inside <${tag}>`)
    }
    if (schema.props.children?.nodes && (children.length || el.children.some(c => c.kind === 'element'))) out.children = children
    if (tag === 'list' && !out.children && el.children.length === 0) delete out.children
    return out
  }

  /** <on:click>actions</on:click> onto out.on */
  handler (el: XmlElement, props: Record<string, Caps>, scope: NameScope, out: Record<string, unknown>): void {
    const event = el.tag.slice(3)
    const on = props.on?.object
    if (!on || !on[event]?.actions) {
      this.issue(el.pos, on ? `<${el.tag}>: events here are ${Object.keys(on).map(e => 'on:' + e).join(', ')}` : `this element has no events`)
      return
    }
    const target = (out.on ??= {}) as Record<string, unknown>
    target[event] = this.actions(el.children, scope)
  }

  /** A child element that is a prop of its parent: <open>, <column>, <option>, <optgroup> */
  childProp (el: XmlElement, prop: string, caps: Caps, scope: NameScope, out: Record<string, unknown>): void {
    if (caps.object) {
      if (out[prop] !== undefined) this.issue(el.pos, `<${el.tag}> is given twice`)
      out[prop] = this.object(el, caps.object, scope)
      return
    }
    if (caps.objects) {
      const list = (out[prop] ??= []) as unknown[]
      if (!Array.isArray(list)) { this.issue(el.pos, `${prop} is already set as an attribute`); return }
      list.push(this.object(el, caps.objects, scope))
    }
  }

  object (el: XmlElement, props: Record<string, Caps>, scope: NameScope): Record<string, unknown> {
    const out: Record<string, unknown> = {}
    this.attrs(el, props, () => scope, out)
    const textProp = TEXT_CONTENT[el.tag]
    if (el.children.some(c => c.kind === 'text')) {
      if (textProp && props[textProp]) out[textProp] = this.guard(() => this.content(el.children, scope), '')
      else this.issue(el.pos, `<${el.tag}> does not take text`)
    }
    for (const child of el.children) {
      if (child.kind !== 'element') continue
      if (child.tag.startsWith('on:')) { this.handler(child, props, scope, out); continue }
      const prop = propForChild(props, child.tag)
      if (prop) { this.childProp(child, prop, props[prop]!, scope, out); continue }
      this.issue(child.pos, `<${child.tag}> is not allowed inside <${el.tag}>`)
    }
    return out
  }

  actions (nodes: XmlNode[], scope: NameScope): Record<string, unknown>[] {
    const out: Record<string, unknown>[] = []
    for (const n of nodes) {
      if (n.kind === 'text') { this.issue(n.pos, 'text is not an action; use <toast> to show a message'); continue }
      const a = this.action(n, scope)
      if (a) out.push(a)
    }
    return out
  }

  action (el: XmlElement, scope: NameScope): Record<string, unknown> | null {
    let name = ACTION_TAGS[el.tag]
    if (!name) { this.issue(el.pos, `unknown action <${el.tag}>; actions are ${Object.keys(ACTION_TAGS).join(', ')}`); return null }
    if (name === 'setState' && el.attrs.some(a => a.name === 'key')) name = 'setIn'
    const schema = this.schema.actions[name]!
    const out: Record<string, unknown> = { do: name }
    if (name === 'callOperation') {
      const input: Record<string, unknown> = {}
      const opName = attrText(el.attrs.find(a => a.name === 'op' || a.name === 'operation'))
      for (const a of el.attrs) {
        if (a.name === 'op' || a.name === 'operation') { out.operation = attrText(a); continue }
        if (a.name === 'into' || a.name === 'when') {
          out[a.name] = this.guard(() => this.attr(a.value, schema.props[a.name]!, scope, a.name), undefined)
          continue
        }
        input[a.name] = this.guard(() => this.inputValue(a, scope, opName, a.name), undefined)
      }
      if (Object.keys(input).length) out.input = input
    } else {
      this.attrs(el, schema.props, () => scope, out)
    }
    const textProp = TEXT_CONTENT[el.tag]
    if (el.children.some(c => c.kind === 'text') && textProp && schema.props[textProp]) out[textProp] = this.guard(() => this.content(el.children, scope), '')
    const elements = el.children.filter((c): c is XmlElement => c.kind === 'element')
    if (name === 'callOperation') {
      for (const child of elements) {
        if (child.tag === 'then' || child.tag === 'catch') out[child.tag] = this.actions(child.children, scope)
        else this.issue(child.pos, `<call> holds <then> and <catch>, not <${child.tag}>`)
      }
    } else if (name === 'openConfirm') {
      out.then = this.actions(elements, scope)
    } else if (elements.length) {
      this.issue(elements[0]!.pos, `<${el.tag}> has no child elements`)
    }
    return out
  }

  /** Operation input and component params: expressions, with plain text typed by the catalog */
  inputValue (a: Attr, scope: NameScope, specOperation: string | null, key: string | null): unknown {
    const v = a.value
    if (v.kind === 'bool') return true
    if (v.kind === 'json') {
      try {
        return parseJsonAttr(v.src)
      } catch {
        throw new MarkupError(`${a.name}: {{…}} holds JSON`, v.pos.line, v.pos.col)
      }
    }
    if (v.kind === 'expr') return this.expr(v.src, scope, v.pos)
    if (v.parts.some(p => 'expr' in p)) return this.template(v.parts, scope)
    const s = v.parts.map(p => (p as { text: string }).text).join('')
    const catalog = specOperation ? this.operations[specOperation] : null
    const field = catalog && key ? OPERATIONS[catalog]?.input?.[key] : null
    const type = (field as { type?: string } | null | undefined)?.type
    if ((type === 'number' || type === 'integer') && NUMERIC.test(s.trim())) return Number(s)
    if (type === 'boolean' && (s === 'true' || s === 'false')) return s === 'true'
    return s
  }

  // -------------------------------------------------------------------------

  root (el: XmlElement): MaxInterfaceSpec | null {
    if (el.tag !== 'tool') {
      this.issue(el.pos, 'the markup is one <tool> element')
      return null
    }
    const rootProps = this.schema.root
    const spec: Record<string, unknown> = { version: 1, kind: 'interface' }
    for (const a of el.attrs) {
      const prop = a.name === 'metadata' ? 'metadataNamespaces' : camel(a.name)
      const caps = rootProps[prop]
      if (!caps || ['version', 'kind', 'body', 'state', 'operations', 'derive', 'components', 'panels', 'icons', 'on'].includes(prop)) {
        this.issue(a.pos, `${a.name} is not an attribute of <tool>; allowed: title, description, locale, preset, capabilities, metadata, metadata-read, refresh`)
        continue
      }
      spec[prop] = this.guard(() => this.attr(a.value, caps, { loops: [], params: [], derived: [], state: [] }, a.name), undefined)
    }

    const elements = el.children.filter((c): c is XmlElement => {
      if (c.kind === 'text') this.issue(c.pos, 'text inside <tool> must be in an element such as <text>')
      return c.kind === 'element'
    })
    const stateNames: string[] = []
    const deriveNames: string[] = []
    for (const child of elements) {
      const name = child.attrs.find(a => a.name === 'name')
      const value = name ? attrText(name) : ''
      if (child.tag === 'state' && value) stateNames.push(value)
      if (child.tag === 'derive' && value) deriveNames.push(value)
      if (child.tag === 'operation' && value) {
        const op = child.attrs.find(a => a.name === 'op' || a.name === 'operation')
        if (op) this.operations[value] = attrText(op)
      }
    }
    const scope: NameScope = { loops: [], params: [], derived: deriveNames, state: stateNames }

    const body: Record<string, unknown>[] = []
    for (const child of elements) {
      switch (child.tag) {
        case 'state': this.declareState(child, spec); break
        case 'operation': this.declareOperation(child, spec); break
        case 'derive': {
          const name = attrText(child.attrs.find(a => a.name === 'name'))
          const value = child.attrs.find(a => a.name === 'value')
          if (!name || !value) { this.issue(child.pos, '<derive> needs name and value'); break }
          const derive = (spec.derive ??= {}) as Record<string, unknown>
          if (name in derive) this.issue(child.pos, `derive ${name} is declared twice`)
          derive[name] = this.guard(() => value.value.kind === 'expr'
            ? this.expr(value.value.src, scope, value.value.pos)
            : this.attr(value.value, { ...emptyCaps(), expr: true }, scope, 'value'), null)
          break
        }
        case 'component': {
          const rawName = attrText(child.attrs.find(a => a.name === 'name'))
          const name = /^[A-Z]/.test(rawName) ? pascalToSnake(rawName) : rawName
          const params = attrText(child.attrs.find(a => a.name === 'params')).split(/[\s,]+/).filter(Boolean)
          if (!name) { this.issue(child.pos, '<component> needs a name'); break }
          for (const a of child.attrs) if (a.name !== 'name' && a.name !== 'params') this.issue(a.pos, `<component> takes name and params, not ${a.name}`)
          const inner: NameScope = { ...scope, params }
          const components = (spec.components ??= {}) as Record<string, unknown>
          components[name] = { ...(params.length ? { params } : {}), body: this.nodes(child.children, inner) }
          break
        }
        case 'icon-def': {
          const icon: Record<string, unknown> = {}
          const name = attrText(child.attrs.find(a => a.name === 'name'))
          if (!name) { this.issue(child.pos, '<icon-def> needs a name'); break }
          for (const a of child.attrs) {
            if (a.name === 'name') continue
            const prop = camel(a.name)
            if (!['path', 'viewBox', 'style'].includes(prop)) { this.issue(a.pos, `<icon-def> takes name, path, view-box and style, not ${a.name}`); continue }
            if (a.value.kind !== 'string') { this.issue(a.pos, `${a.name} is written as text`); continue }
            icon[prop] = attrText(a).trim()
          }
          const icons = (spec.icons ??= {}) as Record<string, unknown>
          if (name in icons) this.issue(child.pos, `icon ${name} is declared twice`)
          icons[name] = icon
          break
        }
        case 'panel': {
          const name = attrText(child.attrs.find(a => a.name === 'name'))
          if (!name) { this.issue(child.pos, '<panel> needs a name'); break }
          const panel: Record<string, unknown> = {}
          this.attrs(child, this.panelProps(), () => scope, panel, ['name'])
          panel.body = this.nodes(child.children, scope)
          const panels = (spec.panels ??= {}) as Record<string, unknown>
          panels[name] = panel
          break
        }
        case 'on:load':
          spec.on = { load: this.actions(child.children, scope) }
          break
        default: {
          const n = this.node(child, scope)
          if (n) body.push(n)
        }
      }
    }
    spec.body = body
    return spec as unknown as MaxInterfaceSpec
  }

  private panelProps (): Record<string, Caps> {
    return {
      title: { ...emptyCaps(), string: true, computed: true },
      size: { ...emptyCaps(), enumValues: ['sm', 'md', 'lg'] }
    }
  }

  nodes (children: XmlNode[], scope: NameScope): Record<string, unknown>[] {
    const out: Record<string, unknown>[] = []
    for (const c of children) {
      if (c.kind === 'text') { this.issue(c.pos, 'text must be inside an element such as <text>'); continue }
      const n = this.node(c, scope)
      if (n) out.push(n)
    }
    return out
  }

  declareState (el: XmlElement, spec: Record<string, unknown>): void {
    const name = attrText(el.attrs.find(a => a.name === 'name'))
    const type = attrText(el.attrs.find(a => a.name === 'type')) || 'string'
    if (!name) { this.issue(el.pos, '<state> needs a name'); return }
    const def: Record<string, unknown> = { type }
    const initial = el.attrs.find(a => a.name === 'initial')
    if (initial) {
      def.initial = this.guard(() => {
        const v = initial.value
        if (v.kind === 'json') return parseJsonAttr(v.src)
        if (v.kind === 'expr') {
          const e = this.expr(v.src, { loops: [], params: [], derived: [], state: [] }, v.pos)
          if (!isConstant(e)) throw new MarkupError('initial is a constant such as {5}, {true} or {["a", "b"]}', v.pos.line, v.pos.col)
          return e
        }
        const s = attrText(initial)
        if (type === 'number') {
          if (!NUMERIC.test(s.trim())) throw new MarkupError('initial must be a number', v.pos.line, v.pos.col)
          return Number(s)
        }
        if (type === 'boolean') return s === 'true'
        return s
      }, undefined)
    }
    for (const a of el.attrs) if (!['name', 'type', 'initial'].includes(a.name)) this.issue(a.pos, `<state> takes name, type and initial, not ${a.name}`)
    const state = (spec.state ??= {}) as Record<string, unknown>
    if (name in state) this.issue(el.pos, `state ${name} is declared twice`)
    state[name] = def
  }

  declareOperation (el: XmlElement, spec: Record<string, unknown>): void {
    const name = attrText(el.attrs.find(a => a.name === 'name'))
    const op = attrText(el.attrs.find(a => a.name === 'op' || a.name === 'operation'))
    if (!name || !op) { this.issue(el.pos, '<operation> needs name and op, e.g. <operation name="list" op="agenda.list"/>'); return }
    const ref: Record<string, unknown> = { operation: op }
    const label = el.attrs.find(a => a.name === 'label')
    if (label) ref.label = attrText(label)
    const confirm = el.attrs.find(a => a.name === 'confirm-above' || a.name === 'confirmAbove')
    if (confirm) ref.confirmAbove = Number(attrText(confirm))
    for (const a of el.attrs) if (!['name', 'op', 'operation', 'label', 'confirm-above', 'confirmAbove'].includes(a.name)) this.issue(a.pos, `<operation> takes name, op, label and confirm-above, not ${a.name}`)
    const operations = (spec.operations ??= {}) as Record<string, unknown>
    if (name in operations) this.issue(el.pos, `operation ${name} is declared twice`)
    operations[name] = ref
  }
}

/** {{[1, 2]}} or {{{"a": 1}}}: the text between the double braces is JSON. {{"a": 1}} also works, as in JSX */
function parseJsonAttr (src: string): unknown {
  const inner = src.startsWith('{') && src.endsWith('}') ? src.slice(1, -1).trim() : src
  try {
    return JSON.parse(inner)
  } catch {
    return JSON.parse(src)
  }
}

function emptyCaps (): Caps {
  return {
    expr: false, computed: false, number: false, integer: false, boolean: false, string: false, enumValues: null,
    responsive: false, nodes: false, actions: false, object: null, objects: null, strings: false, record: false, json: false, required: []
  }
}

function pick (props: Record<string, Caps>, keys: string[]): Record<string, Caps> {
  return Object.fromEntries(keys.filter(k => props[k]).map(k => [k, props[k]!]))
}

function attrText (a: Attr | undefined): string {
  if (!a) return ''
  if (a.value.kind !== 'string') return ''
  return a.value.parts.map(p => (p as { text?: string }).text ?? '').join('')
}

/** The prop a child element stands for: <open> is open, <column> is one of columns */
export function propForChild (props: Record<string, Caps>, tag: string): string | null {
  for (const [prop, caps] of Object.entries(props)) {
    if (caps.object && kebab(prop) === tag && prop !== 'on') return prop
    if (caps.objects && (singular(prop) === tag || (prop === 'options' && tag === 'optgroup'))) return prop
  }
  return null
}

function responsiveMap (s: string, fail: (msg: string) => never): Record<string, number> | number {
  const out: Record<string, number> = {}
  for (const token of s.trim().split(/\s+/)) {
    const m = /^(?:(base|sm|md|lg|xl):)?(\d+)$/.exec(token)
    if (!m) fail(`"${token}" is not a width value; write e.g. "1 md:2 lg:4"`)
    out[m![1] ?? 'base'] = Number(m![2])
  }
  const keys = Object.keys(out)
  return keys.length === 1 && keys[0] === 'base' ? out.base! : out
}

function mergeText (parts: TextPart[]): TextPart[] {
  const out: TextPart[] = []
  for (const p of parts) {
    if ('text' in p) {
      const text = p.text === '\n' ? '\n' : collapse(p.text)
      const last = out[out.length - 1]
      if (last && 'text' in last) last.text = collapse(last.text + text).replace(/ ?\n ?/g, '\n')
      else out.push({ text })
    } else {
      out.push(p)
    }
  }
  return out
}

function trimEdges (parts: TextPart[], which: 'start' | 'end' | 'both' = 'both'): void {
  const first = parts[0]
  if ((which === 'start' || which === 'both') && first && 'text' in first) first.text = first.text.replace(/^ +/, '')
  const last = parts[parts.length - 1]
  if ((which === 'end' || which === 'both') && last && 'text' in last) last.text = last.text.replace(/ +$/, '')
}

function isConstant (e: unknown): boolean {
  if (e === null || typeof e !== 'object') return true
  if (Array.isArray(e)) return e.every(isConstant)
  return false
}

const FAILED = Symbol('failed')

/** One attribute as the compiler reads it, e.g. 'columns="1 md:2"'; FAILED when it does not compile */
export function compileAttribute (source: string, caps: Caps, scope: NameScope): unknown {
  try {
    const a = parseXml(`<x ${source}/>`)[0]?.attrs[0]
    if (!a) return FAILED
    const c = new Compiler()
    const v = c.attr(a.value, caps, scope, a.name)
    return c.issues.length ? FAILED : v
  } catch {
    return FAILED
  }
}

export function compileMarkup (src: string): CompileResult {
  const c = new Compiler()
  let roots: XmlElement[]
  try {
    roots = parseXml(src)
  } catch (err) {
    if (err instanceof MarkupError) return { spec: null, issues: [{ line: err.line, col: err.col, message: err.message.replace(/^\d+:\d+ /, '') }] }
    throw err
  }
  if (roots.length !== 1) {
    return { spec: null, issues: [{ line: roots[1]?.pos.line ?? 1, col: roots[1]?.pos.col ?? 1, message: 'the markup is one <tool> element' }] }
  }
  const spec = c.root(roots[0]!)
  return { spec: c.issues.length ? null : spec, issues: c.issues }
}
